import { z } from "zod";
import { requireSpanishWhenEnglishPresent } from "@portal/common/bilingualSchema";
import { requireValidityOrder } from "@portal/common/validitySchema";

// Mensajes literales de SPEC-219 §9: el servidor devuelve el mismo código si
// el cliente saltea esta validación (mismo criterio que
// `DETAIL_REQUIRED_MESSAGE` en benefits/benefitSchemas.ts). `FIELDS_REQUIRED`
// agrupa título, descripción e imagen en un único mensaje — así lo declara
// `offer.service.ts` (`missingCoreFields`) del lado del servidor.
export const FIELDS_REQUIRED_MESSAGE =
  "Título, descripción e imagen son obligatorios para publicar";

export const CTA_REQUIRED_MESSAGE =
  "Define el texto del botón que verá el miembro";

export const CATEGORY_REQUIRED_MESSAGE = "Selecciona una categoría";

// T5 (qa-bug-matrix-2026-09.md §4.1, #17): un ítem vacío de "Qué incluye"
// pasaba esta validación (solo `.trim()`, sin `.min(1)`) y viajaba al
// servidor, que lo rechazaba con `INVALID_REQUEST` — un código que
// `OffersScreen.tsx` mapea a un mensaje distinto (bajada/nota con inglés sin
// español), así que el operador leía un error que no tenía nada que ver con
// lo que estaba mal. Mismo defecto y mismo patrón que T2 ya cerró en
// benefits/benefitSchemas.ts (`INCLUDE_TEXT_REQUIRED_MESSAGE`): mismo
// literal, deliberado, porque es el mismo campo conceptual en las dos
// pantallas que comparten `IncludesFieldGroup`.
export const INCLUDE_TEXT_REQUIRED_MESSAGE =
  'Cada ítem de "Qué incluye" necesita texto en español';

const includeSchema = z.object({
  textEs: z.string().trim().min(1, INCLUDE_TEXT_REQUIRED_MESSAGE),
  textEn: z.string(),
});

// Hallazgo (revisión en vivo 2026-09-23, mismo defecto que
// `rewardSchemas.ts:DESCRIPTION_ES_REQUIRED_MESSAGE`): `subtitleEs`/`noteEs`
// vacíos con su `en` lleno viajaban igual (C3, comentario de
// `OffersScreen.tsx`) y el servidor los rechazaba con `INVALID_REQUEST`
// genérico — mitigado hasta ahora con un toast fijo
// (`INVALID_REQUEST_MESSAGE`) que no distinguía cuál de los dos campos era.
// Los mensajes repiten los que arma ahora `offer.service.ts` vía
// `fieldRejection`.
export const SUBTITLE_ES_REQUIRED_MESSAGE =
  "La bajada en español es obligatoria cuando hay texto en inglés";
export const NOTE_ES_REQUIRED_MESSAGE =
  "La nota en español es obligatoria cuando hay texto en inglés";

const offerFormObjectSchema = z.object({
  // SPEC-219 §3/§10 criterio 1, PA-037-D: código único, editable solo al
  // crear — de solo lectura al editar (la pantalla lo impone con el campo
  // deshabilitado, no este esquema). Igual se valida acá porque el body lo
  // exige siempre, incluso al editar.
  code: z.string().trim().min(1, "El código es obligatorio"),
  titleEs: z.string().trim().min(1, FIELDS_REQUIRED_MESSAGE),
  titleEn: z.string(),
  subtitleEs: z.string(),
  subtitleEn: z.string(),
  descriptionEs: z.string().trim().min(1, FIELDS_REQUIRED_MESSAGE),
  descriptionEn: z.string(),
  noteEs: z.string(),
  noteEn: z.string(),
  ctaEs: z.string().trim().min(1, CTA_REQUIRED_MESSAGE),
  ctaEn: z.string(),
  categoryId: z.string().trim().min(1, CATEGORY_REQUIRED_MESSAGE),
  imageUrl: z.string().trim().min(1, FIELDS_REQUIRED_MESSAGE),
  // PLAN-007 Fase 4d: id del archivo ya subido y confirmado
  // (`uploadImage`/`FileUploadChip`), con prioridad sobre `imageUrl` en el
  // servidor (OfferInput.imageFileId). Vacío cuando el operador no tocó la
  // imagen en esta sesión del formulario — se sigue enviando la `imageUrl`
  // heredada tal cual estaba.
  imageFileId: z.string(),
  featured: z.boolean(),
  includes: z.array(includeSchema),
  validFrom: z.string(),
  validUntil: z.string(),
  active: z.boolean(),
});

export const offerFormSchema = offerFormObjectSchema.superRefine(
  (values, ctx) => {
    requireSpanishWhenEnglishPresent(values, ctx, [
      {
        esField: "subtitleEs",
        enField: "subtitleEn",
        message: SUBTITLE_ES_REQUIRED_MESSAGE,
      },
      {
        esField: "noteEs",
        enField: "noteEn",
        message: NOTE_ES_REQUIRED_MESSAGE,
      },
    ]);
    requireValidityOrder(values, ctx);
  },
);
export type OfferFormValues = z.infer<typeof offerFormObjectSchema>;

// Mismo criterio que `REWARD_FORM_FIELDS`/`MEMBER_FORM_FIELDS`: la lista de
// claves que `OfferEditor` realmente renderiza, para `applyApiFieldErrors`.
export const OFFER_FORM_FIELDS = Object.keys(
  offerFormObjectSchema.shape,
) as readonly (keyof OfferFormValues)[];
